import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Trash, ShoppingBag, ArrowRight } from '@phosphor-icons/react';

const CartPage = () => {
  const navigate = useNavigate();
  const { fetchCartCount } = useContext(AuthContext);
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await apiClient.get('/cart');
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    try {
      await apiClient.delete(`/cart/${itemId}`);
      toast.success('Item removed from cart');
      fetchCart();
      fetchCartCount();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const calculateTotal = () => {
    return cart.items.reduce((total, item) => {
      return total + (item.product?.base_price || 0) * item.quantity;
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-slate-400">Loading cart...</p>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-24 pb-12">
        <div className="container mx-auto px-8 md:px-16 lg:px-24">
          <div className="text-center py-24" data-testid="empty-cart-message">
            <ShoppingBag size={64} weight="thin" className="mx-auto mb-6 text-slate-400" />
            <h2 className="text-2xl font-normal mb-4 text-foreground">Your cart is empty</h2>
            <p className="text-slate-400 mb-8">Start adding some custom bottles!</p>
            <Link to="/products">
              <Button className="rounded-sm" data-testid="browse-products-button">
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        <h1 className="text-4xl sm:text-5xl font-medium mb-8 text-foreground" data-testid="cart-heading">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
            {cart.items.map((item, index) => (
              <div
                key={item.id}
                className="bg-card border border-border p-6 flex gap-6"
                data-testid={`cart-item-${index}`}
              >
                <img
                  src={item.product?.image_url}
                  alt={item.product?.name}
                  className="w-24 h-24 object-cover"
                  data-testid={`cart-item-image-${index}`}
                />
                <div className="flex-1">
                  <h3 className="text-lg font-normal mb-2 text-foreground" data-testid={`cart-item-name-${index}`}>
                    {item.product?.name}
                  </h3>
                  <p className="text-sm text-slate-400 mb-2" data-testid={`cart-item-size-${index}`}>
                    Size: {item.size}
                  </p>
                  {item.customization && (
                    <div className="text-sm text-slate-300 mb-2" data-testid={`cart-item-customization-${index}`}>
                      <p className="text-[#D4AF37]">Customized</p>
                      {item.customization.custom_text && (
                        <p>Text: {item.customization.custom_text}</p>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-xl font-medium text-[#D4AF37]" data-testid={`cart-item-price-${index}`}>
                      ${((item.product?.base_price || 0) * item.quantity).toFixed(2)}
                    </p>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-400" data-testid={`cart-item-quantity-${index}`}>
                        Qty: {item.quantity}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.id)}
                        data-testid={`remove-item-button-${index}`}
                      >
                        <Trash size={18} weight="regular" className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border p-6 sticky top-24" data-testid="cart-summary">
              <h2 className="text-xl font-normal mb-6 text-foreground">Order Summary</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-slate-300" data-testid="cart-subtotal">
                  <span>Subtotal</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-300" data-testid="cart-shipping">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between text-lg font-medium text-foreground" data-testid="cart-total">
                  <span>Total</span>
                  <span className="text-[#D4AF37]">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
              <Button
                onClick={() => navigate('/checkout')}
                className="w-full rounded-sm bg-[#D4AF37] hover:bg-[#D4AF37]/90"
                data-testid="proceed-to-checkout-button"
              >
                Proceed to Checkout
                <ArrowRight size={20} weight="regular" className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
